import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { ShieldAlert, Fingerprint, Database, Zap, Mail, BarChart, Server, Lock } from 'lucide-react';

export function DiagnosticVaultScreen({ anonymousId, darkMode }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!anonymousId) return;

        const fetchDiag = async () => {
            try {
                setLoading(true);
                const diagData = await api.getGhostDiag(anonymousId);
                setData(diagData);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDiag();
    }, [anonymousId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                <Server className="text-primary-600 mb-4" size={48} />
                <p className="text-sm font-black uppercase tracking-widest text-gray-500">Accessing Deep Vault...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-10 bg-red-500/10 border-2 border-red-500/20 rounded-3xl text-center">
                <ShieldAlert className="mx-auto text-red-500 mb-4" size={48} />
                <h3 className="text-xl font-black text-white uppercase italic mb-2">Access Denied</h3>
                <p className="text-red-400 font-bold text-xs">{error}</p>
            </div>
        );
    }

    const sectionClass = `p-6 md:p-8 rounded-[32px] border transition-all ${darkMode ? 'bg-gray-800/40 border-white/5' : 'bg-white border-gray-100 shadow-sm'}`;
    const labelClass = `text-2xs font-black uppercase tracking-[0.2em] opacity-40 mb-2 block`;
    const valueClass = `text-sm font-mono font-bold break-all ${darkMode ? 'text-white' : 'text-gray-900'}`;

    return (
        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-700 pb-20">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                         <div className="bg-red-500 text-white text-2xs font-black px-2 py-0.5 rounded uppercase tracking-tighter animate-pulse">Debug Mode</div>
                         <h2 className="text-5xl font-black uppercase italic tracking-tighter leading-none text-gray-900 dark:text-white">Deep Vault View</h2>
                    </div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest italic">Technical breakdown of your cryptographical profile.</p>
                </div>
                <button 
                    onClick={() => window.location.reload()} 
                    className="px-6 py-3 bg-primary-600 text-white rounded-xl font-black uppercase text-2xs tracking-widest hover:scale-105 transition-all shadow-lg shadow-purple-900/20"
                >
                    Refresh Sync
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* IDENTITY CORE */}
                <div className={sectionClass}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-3 bg-purple-500/10 rounded-2xl text-primary-600">
                            <Fingerprint size={24} />
                        </div>
                        <h3 className="text-lg font-black uppercase italic tracking-tight text-white">Identity Core</h3>
                    </div>
                    <div className="space-y-6">
                        <div>
                            <span className={labelClass}>Anonymous ID (Hash)</span>
                            <div className={valueClass}>{data.id_hash}</div>
                        </div>
                        <div>
                            <span className={labelClass}>Verified Email (Decrypted)</span>
                            <div className="flex items-center gap-2">
                                <Mail size={14} className="text-primary-600" />
                                <div className={`${valueClass} text-purple-400`}>{data.email_plain}</div>
                            </div>
                        </div>
                        <div>
                            <span className={labelClass}>Hardware Hash (Device)</span>
                            <div className={valueClass}>{data.hardware_hash || 'None'}</div>
                        </div>
                    </div>
                </div>

                {/* REPUTATION & WEIGHTS */}
                <div className={sectionClass}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-3 bg-amber-500/10 rounded-2xl text-amber-500">
                            <BarChart size={24} />
                        </div>
                        <h3 className="text-lg font-black uppercase italic tracking-tight text-white">VANE Engine Metrics</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <span className={labelClass}>Trust Score</span>
                            <div className="text-3xl font-black italic text-white leading-none">{(data.trust_score * 100).toFixed(1)}%</div>
                        </div>
                        <div>
                            <span className={labelClass}>Reputation</span>
                            <div className="text-3xl font-black italic text-white leading-none">{data.reputation_score?.toFixed(1)}</div>
                        </div>
                        <div>
                            <span className={labelClass}>Coin Balance</span>
                            <div className="flex items-center gap-2">
                                <Zap size={18} className="text-amber-500" />
                                <div className="text-3xl font-black italic text-white leading-none">{data.coins}</div>
                            </div>
                        </div>
                        <div>
                            <span className={labelClass}>Shadow Status</span>
                            <div className={`text-xs font-black uppercase px-2 py-1 rounded inline-block ${data.is_shadow_banned ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'}`}>
                                {data.is_shadow_banned ? 'Isolating' : 'Verified Ghost'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* DEMOGRAPHIC TRAITS */}
                <div className={`${sectionClass} md:col-span-2`}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-3 bg-blue-500/10 rounded-2xl text-blue-500">
                            <Database size={24} />
                        </div>
                        <h3 className="text-lg font-black uppercase italic tracking-tight text-white">Saved Demographic Traits</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {Object.entries(data.traits || {}).map(([key, val]) => (
                            <div key={key}>
                                <span className={labelClass}>{key.replace(/_/g, ' ')}</span>
                                <div className={valueClass}>{String(val)}</div>
                            </div>
                        ))}
                        {(!data.traits || Object.keys(data.traits).length === 0) && (
                            <div className="col-span-full py-4 text-gray-500 font-bold italic uppercase text-2xs tracking-widest">No Traits Registered</div>
                        )}
                    </div>
                </div>

                {/* CONFIDENCE MAP */}
                <div className={`${sectionClass} md:col-span-2`}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-3 bg-primary-600/10 rounded-2xl text-primary-600">
                            <Lock size={24} />
                        </div>
                        <h3 className="text-lg font-black uppercase italic tracking-tight text-white">Confidence Map (Weighting)</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {Object.entries(data.confidence_map || {}).map(([key, val]) => (
                            <div key={key}>
                                <span className={labelClass}>{key} Precision</span>
                                <div className="flex items-end gap-2">
                                    <div className="text-2xl font-black italic text-primary-600 leading-none">{val}%</div>
                                    <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden mb-1">
                                        <div className="h-full bg-primary-600" style={{ width: `${val}%` }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="text-center opacity-20 text-2xs font-black uppercase tracking-[0.3em] py-10">
                End of Diagnostic Stream • Confidential Developer Information
            </div>
        </div>
    );
}
